import { useCallback, useEffect, useReducer } from "react"
import DefaultModal from "../../../components/modal/DefaultModal/DefaultModal"
import lockedReducer from "../../../lib/reducer"
import api, { getEnterpriseId } from "../../../lib/api"
import { loader } from "../../../lib/loader"
import { List } from "react-window"

import {
    Box,
    Typography,
    Chip,
    Button,
    Avatar,
    Divider,
    Stack,
    Paper,
    Tooltip
} from "@mui/material"
import GroupsIcon from "@mui/icons-material/Groups"
import PersonIcon from "@mui/icons-material/Person"
import SearchInput from "../../../components/ui/search"
import { RoundedButton } from "../../../components/ui/roundButton"
import CustomSwitch from "../../../components/ui/CustomSwitch"
import { emitSnackbar } from "../../../lib/snackbar-emitter"

const FILTERS = {
    ALL: {
        label: 'Todos',
        filter: (rows, condition = () => true) => rows.filter(row => condition(row))
    },
    MONITORED: {
        label: 'Monitorados',
        filter: (rows, condition = () => true) => rows.filter(row => !row.ignored && condition(row))
    },
    UNMONITORED: {
        label: 'Restritos',
        filter: (rows, condition = () => true) => rows.filter(row => row.ignored && condition(row))
    }
}

const switchSx = {
    width: 46,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: '2px',
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#4caf50',
                opacity: 1,
                border: 0,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 13,
        backgroundColor: '#E9E9EA',
        opacity: 1,
    },
}

const ITEM_HEIGHT = 64

const parseRemoteJid = (remoteJid = '') => {
    const sanitized = remoteJid.startsWith('+') ? remoteJid.slice(1) : remoteJid
    const [rawLocalPart = '', rawDomain = ''] = sanitized.split('@')

    return {
        localPart: rawLocalPart.split(':')[0],
        domain: rawDomain.toLowerCase(),
    }
}

const isPhoneBasedContact = (remoteJid = '') => {
    const { localPart, domain } = parseRemoteJid(remoteJid)

    if (!localPart || !domain) return false
    if (domain === 'g.us') return true
    if (domain === 'lid') return false

    return /^\d{10,15}$/.test(localPart.replace(/\D/g, ''))
}

const getDisplayNumber = (remoteJid = '') => parseRemoteJid(remoteJid).localPart

// Definido fora do componente — recebe items e toggleRow espalhados via rowProps (API v2)
function ContactRow({ index, style, items, toggleRow }) {
    const contact = items[index]
    const isLast = index === items.length - 1

    return (
        <div style={style}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1.2,
                    px: 1,
                    height: ITEM_HEIGHT - 1,
                    borderRadius: 2,
                    transition: "background 0.15s",
                    "&:hover": { bgcolor: "#f5f7fa" },
                }}
            >
                <Avatar
                    src={contact.profilePictureUrl || undefined}
                    sx={{
                        width: 42,
                        height: 42,
                        flexShrink: 0,
                        bgcolor: contact.type === 'GROUP' ? '#e8f5e9' : '#e3f2fd',
                        color: contact.type === 'GROUP' ? '#43a047' : '#1976d2',
                    }}
                >
                    {!contact.profilePictureUrl && (
                        contact.type === 'GROUP'
                            ? <GroupsIcon fontSize="small" />
                            : <PersonIcon fontSize="small" />
                    )}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        noWrap
                        sx={{
                            fontWeight: 600,
                            fontSize: "0.88rem",
                            color: "#1a1a2e",
                            lineHeight: 1.3,
                        }}
                    >
                        {contact.name}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "0.75rem",
                            color: !contact.ignored ? "#43a047" : "#90a4ae",
                            fontWeight: 500,
                            mt: 0.2,
                        }}
                    >
                        {!contact.ignored ? "Conversa monitorada" : "Conversa não monitorada"}
                    </Typography>
                </Box>

                <CustomSwitch
                    sx={switchSx}
                    checked={!contact.ignored}
                    onChange={() => toggleRow(contact.id)}
                />
            </Box>

            {!isLast && (
                <Divider sx={{ ml: "58px", borderColor: "#f0f2f5" }} />
            )}
        </div>
    )
}

const ExclusionList = ({ account, handleClose, handleSuccess }) => {

    const [state, setState] = useReducer(lockedReducer, {
        rows: [],
        filter: 'ALL',
        search: ''
    })

    useEffect(() => {
        const initialize = async () => {
            const enterpriseId = getEnterpriseId()
            loader.on()
            const [settings, contacts] = await Promise.all([
                api.get(`enterprises/${enterpriseId}/scout_accounts/${account.id}/get_settings/`),
                api.get(`enterprises/${enterpriseId}/scout_accounts/${account.id}/get_contacts/`)
            ])

            if (settings.status === 200) {
                const ignore = new Set(settings.data.ignoreList)
                const contacts_merged = (contacts.status === 200 ? contacts.data : [])
                    .filter((each) => isPhoneBasedContact(each.remoteJid))
                setState({
                    rows: contacts_merged.map((each) => ({
                        id: each.remoteJid,
                        phone_num: getDisplayNumber(each.remoteJid),
                        profilePictureUrl: each.pictureUrl || each.profilePictureUrl || each.profilePicUrl,
                        name: each.pushName || each.subject || getDisplayNumber(each.remoteJid) || '',
                        type: each.remoteJid.includes('@g.us') ? 'GROUP' : 'CONTACT',
                        ignored: ignore.has(each.remoteJid)
                    }))
                })
            }

            loader.off()
        }
        initialize()
    }, [account.id])

    const filtered = FILTERS[state.filter].filter(
        state.rows,
        (row) => (row.name || '').toLowerCase().includes(state.search.toLowerCase()) || (row.phone_num || '').includes(state.search)
    )

    const toggleRow = useCallback((id) => {
        setState({
            rows: state.rows.map(row =>
                row.id === id ? { ...row, ignored: !row.ignored } : row
            )
        })
    }, [state.rows])

    const submit = async () => {

        loader.on()
        const enterpriseId = getEnterpriseId()

        //   const addPayload = {
        //     event: "add_ignore",
        //     contact: contact.id,
        // }

        const setIgnored = state.rows.flatMap(row => {
            if (!row.ignored) return []
            return row.id
        })

        const [result] = await Promise.all([
            //api.post(`enterprises/${enterpriseId}/scout_accounts/${account.id}/log_ignore_list/`, addPayload), // Its a mass operation now...
            api.post(`enterprises/${enterpriseId}/scout_accounts/${account.id}/set_ignore_list/`, setIgnored)
        ])

        if (result.status === 202){
            emitSnackbar({message: 'Lista de exclusão salva com sucesso!', severity: 'success'})
            handleSuccess()
            handleClose()
        } else {
            emitSnackbar({message: 'Houve um erro para salvar a lista de exclusão.', severity: 'error'})
        }
        loader.off()
    }

    return (
        <DefaultModal
            title="Lista de exclusão"
            handleClose={handleClose}
            maxWidth="md"
            action={
                <Box width='100%' display='flex' sx={{ justifyContent: 'flex-end' }}>
                    <Box width='50%' display='flex' gap='1rem' >
                        <Box sx={{ width: '100%' }}>
                            <RoundedButton onClick={handleClose} rounded='outlined' sx={{ width: '100%' }}>
                                Cancelar
                            </RoundedButton>
                        </Box>
                        <Box sx={{ width: '100%' }}>
                            <Tooltip title={'Salvar'}>
                                <span style={{ width: '100%' }}>
                                    <RoundedButton sx={{ width: '100%' }} onClick={() => submit(state)}>
                                        Salvar
                                    </RoundedButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            }
        >
            <Box display="flex" gap="1rem" flexDirection="column" height="55vh">
                <Paper
                    elevation={0}
                    sx={{
                        display: "flex",
                        borderRadius: 3,
                        overflow: "hidden",
                        width: "100%",
                        height: "100%",
                    }}
                >
                    {/* ── Sidebar ── */}
                    <Box
                        sx={{
                            flex: 1,
                            flexShrink: 0,
                            p: 2.5,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 700,
                                color: "#333",
                                fontSize: "0.85rem",
                                mb: 1,
                                display: "block",
                            }}
                        >
                            Filtros:
                        </Typography>

                        <Stack spacing={0.8} mb={3}>
                            {Object.entries(FILTERS).map(([key, data]) => {
                                const isActive = state.filter === key
                                return (
                                    <Chip
                                        key={key}
                                        label={`${data.label}  (${data.filter(state.rows).length})`}
                                        onClick={() => setState({ filter: key })}
                                        variant="outlined"
                                        sx={{
                                            justifyContent: "flex-start",
                                            borderRadius: "999px",
                                            fontWeight: 400,
                                            bgcolor: "transparent",
                                            color: isActive ? "rgb(70 171 227)" : "#555",
                                            borderColor: isActive ? "rgb(70 171 227)" : "#c5cae9",
                                            fontSize: "0.82rem",
                                            height: 30,
                                            width: 'fit-content',
                                            cursor: "pointer",
                                            transition: "all 0.15s",
                                            "& .MuiChip-label": { px: 1.5 },
                                            "&:hover": {
                                                bgcolor: "#f0f4ff",
                                                borderColor: "rgb(70 171 227)",
                                            },
                                        }}
                                    />
                                )
                            })}
                        </Stack>

                        <Typography
                            sx={{
                                fontWeight: 700,
                                color: "#333",
                                fontSize: "0.85rem",
                                mb: 1,
                                display: "block",
                            }}
                        >
                            Ações em lote:
                        </Typography>

                        <Stack spacing={1}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setState({ rows: state.rows.map(r => ({ ...r, ignored: false })) })}
                                sx={{
                                    borderRadius: "999px",
                                    textTransform: "none",
                                    fontSize: "0.78rem",
                                    fontWeight: 400,
                                    borderColor: "#c5cae9",
                                    color: "rgb(70 171 227)",
                                    py: 0.6,
                                    px: 2,
                                    width: 'fit-content',
                                    "&:hover": { bgcolor: "#f0f4ff", borderColor: "rgb(70 171 227)" },
                                }}
                            >
                                Marcar todos como monitorados
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setState({ rows: state.rows.map(r => ({ ...r, ignored: true })) })}
                                sx={{
                                    borderRadius: "999px",
                                    textTransform: "none",
                                    fontSize: "0.78rem",
                                    fontWeight: 400,
                                    borderColor: "#c5cae9",
                                    color: "rgb(70 171 227)",
                                    py: 0.6,
                                    px: 2,
                                    width: 'fit-content',
                                    "&:hover": { bgcolor: "#f0f4ff", borderColor: "rgb(70 171 227)" },
                                }}
                            >
                                Marcar todos como restritos
                            </Button>
                        </Stack>
                    </Box>

                    {/* ── Contact List ── */}
                    <Box sx={{ flex: 1, p: 2.5, display: "flex", flexDirection: "column", minWidth: 0 }}>
                        <SearchInput initialValue={state.search} onChange={(value) => setState({ search: value })} />

                        {filtered.length === 0 ? (
                            <Box sx={{ py: 6, textAlign: "center" }}>
                                <Typography sx={{ color: "#ccc", fontSize: "0.88rem" }}>
                                    Nenhum contato encontrado
                                </Typography>
                            </Box>
                        ) : (
                            // v2: List usa ResizeObserver para se auto-dimensionar ao container.
                            // O container precisa de height definido — style inline funciona melhor que sx aqui.
                            <Box style={{ flex: 1, minHeight: 0 }}>
                                <List
                                    rowComponent={ContactRow}
                                    rowCount={filtered.length}
                                    rowHeight={ITEM_HEIGHT}
                                    rowProps={{ items: filtered, toggleRow }}
                                />
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </DefaultModal>
    )
}

export default ExclusionList